<template>
  <div class="collab-creator-guide">
    <!-- Video slides (steps 0-4) -->
    <div v-if="currentStep < videos.length" class="collab-creator-guide__video-slide">
      <h3 class="global-h3 collab-creator-guide__video-title">
        {{ videos[currentStep].title }} <span class="collab-creator-guide__step-label">({{ currentStep + 1 }} of {{ videos.length }})</span>
      </h3>

      <div class="collab-creator-guide__video-container">
        <div class="collab-creator-guide__video-wrap">
          <video
            ref="videoEl"
            class="collab-creator-guide__video"
            :src="videos[currentStep].url"
            :key="currentStep"
            playsinline
            controls
            controlslist="nodownload noplaybackrate"
            disablepictureinpicture="true"
            preload="auto"
            @ended="onVideoEnded"
            @timeupdate="onTimeUpdate"
            @seeking="onSeeking"
            @play="isPlaying = true"
            @pause="isPlaying = false"
          />
          <!-- Next video overlay (shown after video plays through to the end) -->
          <div v-if="videoHasEnded" class="collab-creator-guide__ended-overlay" @click.stop="goToNext">
            <div class="collab-creator-guide__next-overlay">
              <svg width="56" height="56" viewBox="0 0 56 56" fill="none">
                <circle cx="28" cy="28" r="28" fill="rgba(255,255,255,0.9)" />
                <polyline points="23,16 37,28 23,40" fill="none" stroke="#333" stroke-width="4" stroke-linecap="round" stroke-linejoin="round" />
              </svg>
              <span class="collab-creator-guide__next-overlay-label">{{ currentStep < videos.length - 1 ? "Next Video" : "Continue" }}</span>
            </div>
          </div>
          <!-- Play overlay (shown when paused on unwatched videos before they end) -->
          <div v-else-if="!isPlaying" class="collab-creator-guide__play-overlay" @click.stop="playVideo">
            <div class="collab-creator-guide__play-icon">
              <svg width="48" height="48" viewBox="0 0 48 48" fill="none">
                <circle cx="24" cy="24" r="24" fill="rgba(0,0,0,0.5)" />
                <polygon points="18,14 18,34 36,24" fill="#fff" />
              </svg>
            </div>
          </div>
        </div>
      </div>

      <!-- Navigation buttons -->
      <div class="collab-creator-guide__nav-buttons">
        <SrpButton v-if="currentStep > 0" color="gray" fill="outlined" size="small" @click="goToPrev"> Back </SrpButton>
        <div v-else></div>

        <div class="collab-creator-guide__nav-buttons-right">
          <SuperUserActionPanel>
            <LinkWithIcon color="gray" isDottedUnderline @click="skipAllVideos">
              <template #icon><IconEmbedded name="arrow-right_3-5" :size="14" /></template>
              <span>skip all</span>
            </LinkWithIcon>
          </SuperUserActionPanel>
          <SrpButton color="orange" size="small" :isDisabled="!canGoNext" @click="goToNext"> Next </SrpButton>
        </div>
      </div>
    </div>

    <!-- Final confirmation slide (step 4) -->
    <div v-else class="collab-creator-guide__final-slide">
      <div class="collab-creator-guide__final-message">
        <h3 class="global-h3" style="margin-bottom: 8px">You're all set!</h3>
        <p style="margin-bottom: 20px; color: #5b5b5b; font: 14px/20px sans-serif">Reach out any time with questions! <CopyTextNew>creators@shrpa.com</CopyTextNew></p>
      </div>

      <!-- Rewatch links -->
      <div class="collab-creator-guide__rewatch-list">
        <div v-for="(video, index) in videos" :key="index" class="collab-creator-guide__rewatch-item">
          <span>{{ index + 1 }}. {{ video.title }}</span>
          <SrpButton color="gray" fill="outlined" size="tiny" @click="goToStep(index)"> Review </SrpButton>
        </div>
      </div>

      <!-- Navigation buttons -->
      <div class="collab-creator-guide__nav-buttons" style="margin-top: 24px">
        <SrpButton color="gray" fill="outlined" size="small" @click="goToPrev"> Back </SrpButton>

        <SrpButton color="orange" @click="completeGuide" :isDisabled="isLoading"> Got it, I'm ready! </SrpButton>
      </div>
    </div>
  </div>
</template>

<!-- prettier-ignore -->
<script lang="ts">export default { name: "CollabCreatorGuide" };</script>

<script setup lang="ts">
import axios from "axios";
import { ref, computed, watch, nextTick } from "vue";
import { useRoute } from "vue-router";

// Components
import SrpButton from "@components/ui/SrpButton.vue";
import CopyTextNew from "@components/CopyTextNew.vue";
import SuperUserActionPanel from "@components/SuperUserActionPanel.vue";
import LinkWithIcon from "@components/LinkWithIcon.vue";
import IconEmbedded from "@components/ui/IconEmbedded.vue";

const props = withDefaults(
  defineProps<{
    firstCollabVideosDone: number;
  }>(),
  {
    firstCollabVideosDone: 0,
  }
);

const emit = defineEmits<{
  (e: "slideChange"): void;
  (e: "complete"): void;
  (e: "videoProgress", videosDone: number): void;
}>();

const route = useRoute();

const videos = [
  { title: "Intro", url: "https://cdn.shrpa.com/videos/collabguide/creatorguide1.1.mp4" },
  { title: "Visit Planning", url: "https://cdn.shrpa.com/videos/collabguide/creatorguide2.1.mp4" },
  { title: "On Location", url: "https://cdn.shrpa.com/videos/collabguide/creatorguide3.mp4" },
  { title: "Content Delivery", url: "https://cdn.shrpa.com/videos/collabguide/creatorguide4.mp4" },
  { title: "Payment", url: "https://cdn.shrpa.com/videos/collabguide/creatorguide5.1.mp4" },
];

// Step logic: 0-(n-1) = video slides, n = final confirmation
// Initialize to the first unwatched video, or final step if all done
const videoCount = videos.length;
const currentStep = ref(props.firstCollabVideosDone >= videoCount ? videoCount : Math.min(props.firstCollabVideosDone, videoCount - 1));

// Track completed videos locally (starts from server value)
const localVideosDone = ref(props.firstCollabVideosDone);

// Video element ref
const videoEl = ref<HTMLVideoElement | null>(null);

// For unwatched videos: track max reached time to prevent skipping ahead
const maxReachedTime = ref(0);

// Whether the video is currently playing (for play overlay)
const isPlaying = ref(false);

// Whether the current video has reached the end (for next-video overlay)
const videoHasEnded = ref(false);

// Next button enabled: already-completed video OR just finished watching
const canGoNext = computed(() => {
  if (currentStep.value >= videos.length) return false;
  if (currentStep.value < localVideosDone.value) return true;
  return videoHasEnded.value;
});

// Reset state when step changes
watch(currentStep, async () => {
  maxReachedTime.value = 0;
  isPlaying.value = false;
  videoHasEnded.value = false;
  emit("slideChange");

  await nextTick();
  if (currentStep.value < videos.length && videoEl.value) {
    videoEl.value.currentTime = 0;
    // Auto-play the next video
    try {
      await videoEl.value.play();
    } catch {
      // Browser may block autoplay; user can tap the play overlay
    }
  }
});

function playVideo() {
  if (videoEl.value && !isPlaying.value) {
    videoEl.value.play();
  }
}

// Shared anti-skip guard: snaps back if the user seeked past the high-water mark
function enforceMaxReached() {
  if (!videoEl.value) return;
  if (currentStep.value >= localVideosDone.value) {
    const ct = videoEl.value.currentTime;
    if (ct > maxReachedTime.value + 2) {
      videoEl.value.currentTime = maxReachedTime.value;
      return;
    }
    if (ct > maxReachedTime.value) {
      maxReachedTime.value = ct;
    }
  }
}

function onTimeUpdate() {
  enforceMaxReached();
}

function onSeeking() {
  enforceMaxReached();
}

async function onVideoEnded() {
  const videoNumber = currentStep.value + 1; // 1-indexed for API

  // Only call API if this video hasn't been completed yet
  if (currentStep.value >= localVideosDone.value) {
    const uri = `${import.meta.env.VITE_API_URL}/collabs/${route.params.communityId}/inputs/${route.params.collabInputId}/creator/${route.params.creatorId}/collab-guidance-video-progress?videoNumber=${videoNumber}`;
    await axios.put(uri);
    localVideosDone.value = videoNumber;
    emit("videoProgress", videoNumber);
  }

  videoHasEnded.value = true;
}

function goToNext() {
  if (currentStep.value < videos.length) {
    currentStep.value++;
  }
}

function goToPrev() {
  if (currentStep.value > 0) {
    currentStep.value--;
  }
}

function goToStep(step: number) {
  currentStep.value = step;
}

// Complete guide (final button)
const isLoading = ref(false);
async function completeGuide() {
  isLoading.value = true;
  emit("complete");
  isLoading.value = false;
}

// Skip all videos (superuser only)
async function skipAllVideos() {
  const videoNumber = videos.length; // Max video number
  const uri = `${import.meta.env.VITE_API_URL}/collabs/${route.params.communityId}/inputs/${route.params.collabInputId}/creator/${route.params.creatorId}/collab-guidance-video-progress?videoNumber=${videoNumber}&skip=true`;
  await axios.put(uri);
  localVideosDone.value = videoNumber;
  currentStep.value = videoNumber;
  emit("videoProgress", videoNumber);
}
</script>

<style scoped lang="scss">
@import "@/scss/screen-size-ranges.scss";

.collab-creator-guide {
  color: #5b5b5b;
  font: 14px/18px sans-serif;

  &__video-slide {
    display: flex;
    flex-direction: column;
    align-items: center;
  }

  &__video-title {
    margin-bottom: 12px;
    text-align: center;
    display: inline-flex;
    align-items: center;
    gap: 6px;
  }

  &__video-container {
    width: 100%;
    max-height: 70vh;
    margin: 0 auto 16px;
    display: flex;
    justify-content: center;
  }

  &__video-wrap {
    position: relative;
    display: inline-flex;
    border-radius: 8px;
    overflow: hidden;
  }

  &__play-overlay {
    position: absolute;
    inset: 0;
    display: flex;
    justify-content: center;
    align-items: center;
    cursor: pointer;
  }

  &__ended-overlay {
    position: absolute;
    inset: 0;
    display: flex;
    justify-content: center;
    align-items: center;
    cursor: pointer;
    background: rgba(0, 0, 0, 0.45);
    z-index: 2;
  }

  &__play-icon {
    opacity: 0.9;
    transition: opacity 0.15s ease;

    &:hover {
      opacity: 1;
    }
  }

  &__next-overlay {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 8px;
    transition: transform 0.15s ease;

    &:hover {
      transform: scale(1.05);
    }
  }

  &__next-overlay-label {
    color: #fff;
    font:
      600 14px/1 "Quicksand",
      sans-serif;
  }

  &__nav-right {
    display: flex;
    align-items: center;
    gap: 10px;
  }

  &__step-label {
    margin-top: 3px;
    color: #888;
    font: 500 13px/18px sans-serif;
    white-space: nowrap;
  }

  &__video {
    width: auto;
    max-width: 100%;
    max-height: 70vh;
    min-height: 500px;
    object-fit: contain;
    display: block;
    border: 1px solid rgba(0, 0, 0, 0.1);
    box-shadow: 0 3px 12px rgba(0, 0, 0, 0.15);
    border-radius: 8px;
  }

  &__nav-buttons {
    width: 100%;
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 12px;
  }

  &__nav-buttons-right {
    display: flex;
    align-items: center;
    gap: 12px;
  }

  &__final-slide {
    display: flex;
    flex-direction: column;
  }

  &__final-message {
    text-align: center;
    margin-top: 13px;
  }

  &__rewatch-list {
    padding: 16px;
    border-radius: 8px;
    background: #f5f5f5;
  }

  &__rewatch-item {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 8px 0;
    border-bottom: 1px solid rgba(0, 0, 0, 0.08);
    font: 14px/20px sans-serif;

    &:last-child {
      border-bottom: none;
    }
  }
}

// mobile
@media (max-width: $mobile-max-width) {
  .collab-creator-guide {
    &__video-container {
      max-height: 65vh;
      border-radius: 4px;
    }

    &__video {
      max-height: 65vh;
    }
  }
}
</style>
